import React from 'react';
import PropTypes from 'prop-types';

/**
 * #669 — the approval trail's tamper-evidence verdict, shown where the approval
 * record already is.
 *
 * WHY THIS EXISTS. `verify_approval_trail` has existed since #590 and the route
 * since #630/#639, but nothing in the UI ever called it. So a tampered chain was
 * discoverable only by someone who already suspected it and knew the endpoint
 * existed — which is the original complaint in #630 narrowed rather than removed.
 * #602 then made GDPR erasure a *declared, witnessed* rewrite of the chain, a
 * design that assumes a reader can reconcile the chain against the witness rows.
 * The route makes that possible; surfacing it makes it likely.
 *
 * WHY NOTHING RENDERS ON SUCCESS. A green tick on every item would be noise, and
 * noise is how you train a reader to stop seeing a control. The only states worth
 * a reader's attention are "broken" and "could not tell", so those are the only
 * ones that paint. An intact chain is the overwhelmingly common case and says
 * nothing a reader needs.
 *
 * WHY `null` IS ITS OWN STATE, not folded into either boolean. "The chain is
 * broken" and "I could not determine whether the chain is broken" are different
 * facts. The route 404s for an unknown or out-of-scope item, and a 404 read as
 * "verified" would be the worst possible failure: silence that looks like
 * assurance. So an absent or unusable verdict renders as INDETERMINATE, visibly,
 * rather than as nothing at all.
 *
 * Visible to every reader who can see the item (owner decision, 2026-07-28). The
 * reasoning: the person misled by a silently tampered chain is whoever is relying
 * on "this is approved", not the approver — and for an MCP agent acting on that
 * state, the consequence is silent and at scale.
 */
function ApprovalTrailVerdict({ verdict, itemId }) {
  // Not yet loaded: render nothing rather than flashing an alarm that resolves to
  // "fine" a moment later. `undefined` is "no answer yet"; `{ok: null}` is a real,
  // settled answer meaning "could not determine", and those are not the same.
  if (verdict === undefined || verdict === null) return null;
  if (verdict.ok === true) return null;

  const indeterminate = verdict.ok !== false;
  const broken = Array.isArray(verdict.broken_at) ? verdict.broken_at.length : 0;
  // #695: a chain can now fail verification with NOTHING in `broken_at`. Tail
  // truncation leaves every surviving link internally valid, so it is caught by
  // reconciling the chain head against the live approval rows rather than by the hash
  // walk. Routing that through the "altered" wording below would have rendered
  // "0 recorded decisions have been altered" — which reads as a bug in the checker
  // rather than a finding about the record, and buries the more serious of the two.
  const missing = Array.isArray(verdict.incomplete_for) ? verdict.incomplete_for.length : 0;

  return (
    <div
      className={`rmdv-trail-verdict ${indeterminate ? 'is-unknown' : 'is-broken'}`}
      data-testid={`approval-trail-verdict-${itemId}`}
      data-verdict={indeterminate ? 'indeterminate' : 'broken'}
      // The two failure kinds stay distinguishable to a test and to anything reading
      // the DOM, without inventing a third visual state — both mean "do not rely on the
      // approvals below", which is the only decision the reader actually has to make.
      data-broken-count={broken}
      data-missing-count={missing}
      // `alert` rather than `status`: a broken approval chain is not an
      // incidental status update, and a reader arriving by screen reader should
      // not have to go looking for it.
      role="alert"
    >
      <span aria-hidden="true">⚠</span>
      {indeterminate && (
        <span>
          Approval trail could not be verified — treat this item’s sign-off as
          unconfirmed.
        </span>
      )}
      {!indeterminate && broken > 0 && (
        <span>
          Approval trail is broken — {broken === 1 ? 'one recorded decision has'
            : `${broken} recorded decisions have`} been altered since it was signed
          off. The approvals below cannot be relied on.
        </span>
      )}
      {!indeterminate && broken === 0 && missing > 0 && (
        <span>
          Approval trail is incomplete — the record for {missing === 1 ? 'one reviewer'
            : `${missing} reviewers`} is missing its most recent
          {missing === 1 ? ' decision' : ' decisions'}, so a withdrawal or rejection may
          have been removed. The approvals below cannot be relied on.
        </span>
      )}
      {/* `ok: false` with neither count populated cannot arise from
          `verify_approval_trail` (ok is derived from exactly these two lists), but it
          can arise from a hand-built verdict or a future field. An alert box with no
          sentence in it would be worse than any wording, so this branch always says
          something. */}
      {!indeterminate && broken === 0 && missing === 0 && (
        <span>
          Approval trail failed verification. The approvals below cannot be relied on.
        </span>
      )}
    </div>
  );
}

ApprovalTrailVerdict.propTypes = {
  /** `{ok, events, broken_at}` from `requirementsApi.verifyApprovalTrail`, or
   *  `{ok: null}` for a settled "could not determine". `undefined` = not loaded
   *  yet, which renders nothing. */
  verdict: PropTypes.shape({
    ok: PropTypes.bool,
    events: PropTypes.number,
    broken_at: PropTypes.array,
    // #695: reviewers whose surviving chain contradicts the live approval rows —
    // i.e. events removed from the END of the chain, which the hash walk cannot see.
    incomplete_for: PropTypes.array,
    // Reviewers with a live decision and no chain at all (pre-#590 data). Reported for
    // completeness; deliberately NOT a failure, because legacy is not tampering.
    untrailed_reviewers: PropTypes.array,
  }),
  itemId: PropTypes.string.isRequired,
};

export default ApprovalTrailVerdict;
